import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { FRCallback, FRStep, StepType } from '@forgerock/javascript-sdk/lib-esm';
import { FrLoginInterface } from '@shared/lib/models/fr-login-interface.enum';
import { FrRealmSteps } from '@shared/lib/models/fr-realm-steps';
import { UsernameScaled } from '@shared/lib/models/username-scaled';
import { AnalyticsService } from '@shared/lib/services/analytics.service';
import { FrSdkService } from '@shared/lib/services/fr-sdk.service';
import { InterruptTransferService } from '@shared/lib/services/interrupt-transfer.service';
import { LoginContextService } from '@shared/lib/services/login-context.service';
import { SharedLoginConstants } from '@shared/lib/shared-login.constants';
import { ID_TOKEN_NAME, LOGIN_REALM, LOGIN_SERVICE_URL, LoginResponse, SsoAuthService } from '@usana/ux/sso-auth';
import { ReplaySubject, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private readonly stepSubject: ReplaySubject<FRStep>;

  private readonly defaultRealm: string;
  private stepsPerRealm: { [realm: string]: FrRealmSteps };

  constructor(
    private readonly httpClient: HttpClient,
    private readonly frSdkService: FrSdkService,
    private readonly loginContextService: LoginContextService,
    private readonly interruptTransferService: InterruptTransferService,
    private readonly ssoAuthService: SsoAuthService,
    private readonly analyticsService: AnalyticsService,
    @Inject(LOGIN_REALM) private readonly loginRealm: string,
    @Inject(LOGIN_SERVICE_URL) private readonly loginServiceUrl: string,
    @Inject(ID_TOKEN_NAME) private readonly idTokenName: string
  ) {
    // NOSONAR
    this.stepSubject = new ReplaySubject<FRStep>(1);
    this.defaultRealm = loginRealm;
    this.reset();
  }

  getStepSub(): Subject<FRStep> {
    return this.stepSubject;
  }

  start(): Subject<FRStep> {
    this.nextStep(); // start the process
    return this.getStepSub();
  }

  reset(): void {
    this.stepSubject.next(null);
    this.stepsPerRealm = {};
    this.switchToRealm(this.defaultRealm); // switch to the default realm if we aren't already on it.
  }

  stepToInterface(step: FRStep): FrLoginInterface {
    if (!step) {
      return FrLoginInterface.NONE;
    }

    switch (step.type.toString()) {
      case StepType.LoginSuccess:
        return FrLoginInterface.SUCCESS;
      case StepType.LoginFailure:
        return FrLoginInterface.FAILURE;
      default:
        return this.callbacksToInterface(step.callbacks);
    }
  }

  currentStep(): FRStep {
    const realm = this.frSdkService.currentRealm();
    if (this.stepsPerRealm[realm] && this.stepsPerRealm[realm].steps.length > 0) {
      return this.stepsPerRealm[realm].steps[this.stepsPerRealm[realm].steps.length - 1];
    }

    return null;
  }

  stepCount(): number {
    const realm = this.frSdkService.currentRealm();
    if (this.stepsPerRealm[realm]) {
      return this.stepsPerRealm[realm].steps.length;
    }
    return 0;
  }

  previousStep(updateSubject: boolean): FRStep {
    const realm = this.frSdkService.currentRealm();
    if (this.stepsPerRealm[realm]) {
      // remove a step
      const step: FRStep = this.stepsPerRealm[realm].steps.pop();
      if (updateSubject) {
        // update the subject to the previous so we don't go back
        // we don't need to save it since its already in the array
        this.stepSubject.next(this.currentStep());
      }
      return step;
    }
    return null;
  }

  nextStep(): void {
    this.frSdkNext(1);
  }

  /**
   * go through steps until we reach the step requested, steps will be saved but they will not be added to the replay subject
   * NOTE: this is a somewhat dangerous method
   * so only use it if you for sure know what you are doing
   * @param frInterface the interface to stop on
   */
  accelerateToStepWithInterface(frInterface: FrLoginInterface): Promise<FRStep> {
    // if we are already on the step then that is great
    const currInterface = this.stepToInterface(this.currentStep());
    if (currInterface === frInterface) {
      return Promise.resolve(this.currentStep());
    } else {
      // todo determine if we need to select anything here
    }

    return new Promise<FRStep>((resolve) => {
      // call the next step
      this.frSdkService.next(this.currentStep()).then((step: any) => {
        this.saveOnlyTheGoodSteps(step);
        // recurse
        this.accelerateToStepWithInterface(frInterface).then((retStep: FRStep) => {
          resolve(retStep);
        });
      });
    });
  }

  switchToRealm(realm: string): void {
    this.frSdkService.patchConfigOptions({
      realmPath: realm,
    });
    this.setupRealm(realm);
  }

  correctUsername(username: string): Promise<UsernameScaled> {
    return this.httpClient
      .get<UsernameScaled>(`${this.loginServiceUrl}/username/scale`, {
        params: {
          username: username,
        },
      })
      .toPromise();
  }

  convertToken(tokenId: string): Promise<HttpResponse<LoginResponse>> {
    return this.httpClient
      .post<LoginResponse>(
        `${this.loginServiceUrl}/login/${this.loginRealm}/convertToken`,
        {
          tokenId: tokenId,
          mobile: this.loginContextService.isMobileAppLogin(),
          kiosk: this.loginContextService.isKioskMode(),
        },
        {
          observe: 'response',
          withCredentials: true,
        }
      )
      .toPromise();
  }

  oidcLogin(username: string, password: string): Promise<boolean> {
    return this.httpClient
      .post<LoginResponse>(
        `${this.loginServiceUrl}/login/${this.loginRealm}/oidc`,
        {
          username: username,
          password: password,
          mobile: this.loginContextService.isMobileAppLogin(),
          kiosk: this.loginContextService.isKioskMode(),
        },
        {
          observe: 'response',
          withCredentials: true,
        }
      )
      .pipe(map((loginResponse) => this.handleLoginResponse(loginResponse)))
      .toPromise();
  }

  handleLoginResponse(loginResponse: HttpResponse<LoginResponse>): boolean {
    if (loginResponse.body) {
      if (!loginResponse.body.success) {
        this.analyticsService.trackEvent(SharedLoginConstants.ANALYTICS_EVENTS.LOGIN_RESULT, {
          is_success: loginResponse.body.success,
          fail_reason: loginResponse.body.tag || 'invalidLogin',
        });
        return false;
      }
      // successful login!
      this.analyticsService.trackEvent(SharedLoginConstants.ANALYTICS_EVENTS.LOGIN_RESULT, {
        is_success: true,
      });
      if (loginResponse.body.redirectUri) {
        this.loginContextService.storeRedirectUri(loginResponse.body.redirectUri);
      }
      if (loginResponse.body.interrupts.length > 0) {
        // error but we want to also go
        this.interruptTransferService.handleInterrupts(
          loginResponse.headers.get(environment.app.ssoAccessToken),
          loginResponse.body.interrupts
        );
        return true;
      } else {
        // capture the login tokens, note interrupt case doesn't do this because it will grab them when interrupts are finished.
        if (loginResponse.headers.has(this.idTokenName)) {
          const idToken: string = loginResponse.headers.get(this.idTokenName);
          this.ssoAuthService.setUserFromIdToken(idToken);
          this.loginContextService.redirectPostLoginWithTokens({
            idToken: idToken,
            refreshToken: '',
            coreToken: '',
          });
          return true;
        } else if (loginResponse.body.tokens?.idToken) {
          // this is a mobile login, we don't want to set the id-token in browser since we are going back to the app
          this.loginContextService.redirectPostLoginWithTokens(loginResponse.body.tokens);
          return true;
        }
      }
    }
    return false;
  }

  private frSdkNext(timesSubmitted: number): void {
    this.frSdkService
      .next(this.currentStep())
      .then((step: any) => {
        const frInterface: FrLoginInterface = this.saveOnlyTheGoodSteps(step);
        if (frInterface === FrLoginInterface.FAILURE) {
          if (timesSubmitted === 1 && step.payload.code === 400) {
            // possible timeout.
            // submit with null to get a new authId
            this.replaceCurrentStepAuthIdWithFresh().then(() => {
              this.frSdkNext(timesSubmitted + 1); // submit it again with the new authId
            });
            return; // early return so we don't add to the subject
          }
        }
        this.stepSubject.next(step as FRStep);
      })
      .catch((err) => {
        this.stepSubject.error(err);
      });
  }

  private replaceCurrentStepAuthIdWithFresh(): Promise<void> {
    return this.frSdkService.next(null).then((step: any) => {
      // pop the previous, but don't update the subject
      const submittedStep: FRStep = this.previousStep(false);
      submittedStep.payload.authId = step.payload.authId;
      this.saveStep(submittedStep);
    });
  }

  private setupRealm(realm: string): void {
    if (!this.stepsPerRealm[realm]) {
      this.stepsPerRealm[realm] = new FrRealmSteps();
    }
  }

  private callbacksToInterface(callbacks: FRCallback[]): FrLoginInterface {
    const cbList = callbacks.map((frc) => frc.getType()).join(',');
    // todo is this specific enough?
    switch (cbList) {
      case 'NameCallback,PasswordCallback':
        return FrLoginInterface.USERNAME_PASSWORD;
      case 'ChoiceCallback':
        return FrLoginInterface.CHOICE;
      default:
        return FrLoginInterface.UNKNOWN;
    }
  }

  private saveOnlyTheGoodSteps(step: FRStep): FrLoginInterface {
    const frInterface: FrLoginInterface = this.stepToInterface(step);
    if (frInterface !== FrLoginInterface.FAILURE && frInterface !== FrLoginInterface.SUCCESS) {
      // if its failure or success we don't want to save it so we cn resubmit.
      this.saveStep(step);
    }
    return frInterface;
  }

  private saveStep(newStep: FRStep): void {
    const realm = this.frSdkService.currentRealm();
    if (this.stepsPerRealm[realm]) {
      this.stepsPerRealm[realm].steps.push(newStep);
    }
  }
}
